// Hook for managing modifier sequencing and overrides
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { 
  applyModifiers, 
  applyDemoModifiers,
  buildCodeString,
  type ModifierSequencingInput,
  type ModifierSequencingResult,
} from '@/lib/modifiers/modifierSequencing';
import type { SiteOfService, Laterality } from '@/lib/modifiers/types';

export interface ModifierApplicationRecord {
  id: string;
  authorization_id: string;
  reconciliation_entry_id: string;
  cpt_code: string;
  applied_modifiers: string[];
  modifier_explanations: Record<string, string>;
  final_code_string: string;
  matched_rule_ids: string[];
  payer_specific_rules_applied: boolean;
  is_overridden: boolean;
  override_by: string | null;
  override_at: string | null;
  override_reason: string | null;
  original_modifiers: string[];
  original_code_string: string | null;
  is_demo_entry: boolean;
  created_at: string;
  updated_at: string;
}

export interface OverrideLogEntry {
  id: string;
  modifier_application_id: string;
  authorization_id: string;
  cpt_code: string;
  action: string;
  previous_modifiers: string[];
  new_modifiers: string[];
  previous_code_string: string;
  new_code_string: string;
  actor_id: string;
  actor_role: string;
  reason: string;
  created_at: string;
}

interface UseModifierSequencingReturn {
  applications: ModifierApplicationRecord[];
  overrideLogs: OverrideLogEntry[];
  loading: boolean;
  error: string | null;
  fetchApplications: (authorizationId: string) => Promise<void>;
  applyModifiersToEntry: (
    authorizationId: string,
    reconciliationEntryId: string,
    cptCode: string,
    input: Omit<ModifierSequencingInput, 'cptCode'>
  ) => Promise<ModifierApplicationRecord | null>;
  overrideModifiers: (
    applicationId: string,
    newModifiers: string[],
    reason: string,
    actorId: string,
    actorRole: string
  ) => Promise<boolean>;
  revertOverride: (
    applicationId: string,
    reason: string,
    actorId: string,
    actorRole: string
  ) => Promise<boolean>;
  autoApplyAll: (
    authorizationId: string,
    entries: Array<{
      reconciliationEntryId: string;
      cptCode: string;
      siteOfService: SiteOfService | null;
      laterality: Laterality | null;
      documentationText?: string;
      existingModifiers?: string[];
    }>
  ) => Promise<void>;
  getExportReadyApplications: (authorizationId: string) => ModifierApplicationRecord[];
  fetchOverrideLogs: (authorizationId: string) => Promise<void>;
}

export function useModifierSequencing(): UseModifierSequencingReturn {
  const [applications, setApplications] = useState<ModifierApplicationRecord[]>([]);
  const [overrideLogs, setOverrideLogs] = useState<OverrideLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isDemoMode } = useDemoMode();

  const fetchApplications = useCallback(async (authorizationId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('cpt_modifier_applications')
        .select('*')
        .eq('authorization_id', authorizationId)
        .order('cpt_code', { ascending: true });

      if (queryError) throw queryError;
      
      // Type-safe transformation
      const typedData: ModifierApplicationRecord[] = (data || []).map(row => ({
        id: row.id,
        authorization_id: row.authorization_id,
        reconciliation_entry_id: row.reconciliation_entry_id,
        cpt_code: row.cpt_code,
        applied_modifiers: row.applied_modifiers || [],
        modifier_explanations: (row.modifier_explanations as Record<string, string>) || {},
        final_code_string: row.final_code_string,
        matched_rule_ids: row.matched_rule_ids || [],
        payer_specific_rules_applied: row.payer_specific_rules_applied || false,
        is_overridden: row.is_overridden || false,
        override_by: row.override_by,
        override_at: row.override_at,
        override_reason: row.override_reason,
        original_modifiers: row.original_modifiers || [],
        original_code_string: row.original_code_string,
        is_demo_entry: row.is_demo_entry || false,
        created_at: row.created_at,
        updated_at: row.updated_at,
      }));
      
      setApplications(typedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch modifier applications');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOverrideLogs = useCallback(async (_authorizationId: string) => {
    // Table removed — return empty
    console.debug('Skipping cpt_modifier_override_log (table removed)');
    setOverrideLogs([]);
  }, []);

  const applyModifiersToEntry = useCallback(async (
    authorizationId: string,
    reconciliationEntryId: string,
    cptCode: string,
    input: Omit<ModifierSequencingInput, 'cptCode'>
  ): Promise<ModifierApplicationRecord | null> => {
    setLoading(true);
    setError(null);
    try {
      // Apply modifiers using the sequencing engine
      const sequencingInput: ModifierSequencingInput = {
        cptCode,
        ...input,
      };
      
      const result = isDemoMode 
        ? applyDemoModifiers(sequencingInput) 
        : applyModifiers(sequencingInput);

      // Upsert the application record
      const { data, error: upsertError } = await supabase
        .from('cpt_modifier_applications')
        .upsert({
          authorization_id: authorizationId,
          reconciliation_entry_id: reconciliationEntryId,
          cpt_code: cptCode,
          applied_modifiers: result.appliedModifiers.map(m => m.modifier),
          modifier_explanations: result.modifierExplanations,
          final_code_string: result.finalCodeString,
          matched_rule_ids: result.matchedRuleIds,
          payer_specific_rules_applied: result.payerSpecificRulesApplied,
          is_demo_entry: isDemoMode,
        }, {
          onConflict: 'reconciliation_entry_id,cpt_code',
        })
        .select()
        .single();

      if (upsertError) throw upsertError;
      
      // Type-safe transformation
      const record: ModifierApplicationRecord = {
        id: data.id,
        authorization_id: data.authorization_id,
        reconciliation_entry_id: data.reconciliation_entry_id,
        cpt_code: data.cpt_code,
        applied_modifiers: data.applied_modifiers || [],
        modifier_explanations: (data.modifier_explanations as Record<string, string>) || {},
        final_code_string: data.final_code_string,
        matched_rule_ids: data.matched_rule_ids || [],
        payer_specific_rules_applied: data.payer_specific_rules_applied || false,
        is_overridden: data.is_overridden || false,
        override_by: data.override_by,
        override_at: data.override_at,
        override_reason: data.override_reason,
        original_modifiers: data.original_modifiers || [],
        original_code_string: data.original_code_string,
        is_demo_entry: data.is_demo_entry || false,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };
      
      // Update local state
      setApplications(prev => {
        const existing = prev.findIndex(
          a => a.reconciliation_entry_id === reconciliationEntryId && a.cpt_code === cptCode
        );
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = record;
          return updated;
        }
        return [...prev, record];
      });
      
      return record;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply modifiers');
      return null;
    } finally {
      setLoading(false);
    }
  }, [isDemoMode]);

  const overrideModifiers = useCallback(async (
    applicationId: string,
    newModifiers: string[],
    reason: string,
    actorId: string,
    actorRole: string
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      // Get current application
      const { data: current, error: fetchError } = await supabase
        .from('cpt_modifier_applications')
        .select('*')
        .eq('id', applicationId)
        .single();

      if (fetchError) throw fetchError;

      const newCodeString = buildCodeString(current.cpt_code, newModifiers);

      // Update the application
      const { error: updateError } = await supabase
        .from('cpt_modifier_applications')
        .update({
          applied_modifiers: newModifiers,
          final_code_string: newCodeString,
          is_overridden: true,
          override_by: actorId,
          override_at: new Date().toISOString(),
          override_reason: reason,
          original_modifiers: current.applied_modifiers,
          original_code_string: current.final_code_string,
        })
        .eq('id', applicationId);

      if (updateError) throw updateError;

      // Override log table removed — skip
      console.debug('Skipping cpt_modifier_override_log (table removed)');

      // Refresh applications
      await fetchApplications(current.authorization_id);
      await fetchOverrideLogs(current.authorization_id);
      
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to override modifiers');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchApplications, fetchOverrideLogs]);

  const revertOverride = useCallback(async (
    applicationId: string,
    reason: string,
    actorId: string,
    actorRole: string
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      // Get current application
      const { data: current, error: fetchError } = await supabase
        .from('cpt_modifier_applications')
        .select('*')
        .eq('id', applicationId)
        .single();

      if (fetchError) throw fetchError;

      if (!current.is_overridden || !current.original_modifiers) {
        throw new Error('No override to revert');
      }

      const revertedCodeString = buildCodeString(current.cpt_code, current.original_modifiers);

      // Update the application
      const { error: updateError } = await supabase
        .from('cpt_modifier_applications')
        .update({
          applied_modifiers: current.original_modifiers,
          final_code_string: revertedCodeString,
          is_overridden: false,
          override_by: null,
          override_at: null,
          override_reason: null,
          original_modifiers: [],
          original_code_string: null,
        })
        .eq('id', applicationId);

      if (updateError) throw updateError;

      // Override log table removed — skip
      console.debug('Skipping cpt_modifier_override_log (table removed)');

      // Refresh applications
      await fetchApplications(current.authorization_id);
      await fetchOverrideLogs(current.authorization_id);
      
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revert override');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchApplications, fetchOverrideLogs]);

  const autoApplyAll = useCallback(async (
    authorizationId: string,
    entries: Array<{
      reconciliationEntryId: string;
      cptCode: string;
      siteOfService: SiteOfService | null;
      laterality: Laterality | null;
      documentationText?: string;
      existingModifiers?: string[];
    }>
  ) => {
    setLoading(true);
    setError(null);
    try {
      for (const entry of entries) {
        await applyModifiersToEntry(
          authorizationId,
          entry.reconciliationEntryId,
          entry.cptCode,
          {
            siteOfService: entry.siteOfService,
            laterality: entry.laterality,
            documentationText: entry.documentationText,
            existingModifiers: entry.existingModifiers,
          }
        );
      }
      await fetchApplications(authorizationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to auto-apply modifiers');
    } finally {
      setLoading(false);
    }
  }, [applyModifiersToEntry, fetchApplications]);

  const getExportReadyApplications = useCallback((authorizationId: string) => {
    return applications.filter(a => a.authorization_id === authorizationId);
  }, [applications]);

  return {
    applications,
    overrideLogs,
    loading,
    error,
    fetchApplications,
    applyModifiersToEntry,
    overrideModifiers,
    revertOverride,
    autoApplyAll,
    getExportReadyApplications,
    fetchOverrideLogs,
  };
}
